/**
 * Cookie-based session auth composable for the Vue frontend.
 *
 * Talks to the `/auth/session/*` endpoints in `services/chatbot/app/routers/
 * auth_session.py`. The browser only ever sees an httpOnly login_id cookie;
 * the underlying access token lives server-side.
 *
 * Bearer-token auth (`/auth/login`, `Authorization: Bearer ...`) remains the
 * contract for programmatic API consumers and is intentionally untouched.
 */

import { ref, computed } from 'vue'
import { apiFetch, onAuthError, resetAuthErrorState } from '@/services/api'
import type { SessionIdentity } from '@/types'

const userName = ref('')
const userEmail = ref('')
const subjectId = ref('')
const clientId = ref('')
const showLoginPage = ref(false)
const authChecked = ref(false)
const loginError = ref('')

// --- Session status watcher ------------------------------------------------
// Polls GET /auth/session/status so Vision-side revocation (admin force-
// logout, entitlement removal) surfaces as a login prompt within ~a minute
// instead of waiting for the next 401. The backend caches its introspect
// heartbeat (~5 min), so this poll is cheap.
const STATUS_POLL_INTERVAL_MS = 60_000

let statusTimer: ReturnType<typeof setInterval> | null = null
let visibilityListenerAttached = false

function clearIdentity() {
  userName.value = ''
  userEmail.value = ''
  subjectId.value = ''
  clientId.value = ''
}

function onSessionInvalid() {
  stopSessionWatch()
  clearIdentity()
  showLoginPage.value = true
}

async function checkSessionStatus() {
  try {
    const res = await apiFetch('/auth/session/status')
    if (!res.ok) return // transient backend error — fail open, keep session
    const data: { valid?: boolean } = await res.json()
    if (data.valid === false) onSessionInvalid()
  } catch {
    // Network error — fail open; the next API 401 still triggers re-auth.
  }
}

function handleVisibilityChange() {
  // Idle tabs miss interval ticks; re-check as soon as the user returns.
  if (document.visibilityState === 'visible' && statusTimer !== null) {
    void checkSessionStatus()
  }
}

function startSessionWatch() {
  if (statusTimer !== null) return
  statusTimer = setInterval(() => void checkSessionStatus(), STATUS_POLL_INTERVAL_MS)
  if (!visibilityListenerAttached) {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    visibilityListenerAttached = true
  }
}

function stopSessionWatch() {
  if (statusTimer !== null) {
    clearInterval(statusTimer)
    statusTimer = null
  }
}

function consumeLoginErrorParam() {
  // The session callback redirects with ?auth_error=entitlement when jarvis-be
  // rejects the login (authenticated, but no JarvisGPT entitlement). A `reason`
  // param distinguishes the two denial causes so the copy is precise:
  //   - `service`    → the subscription doesn't include JarvisGPT
  //   - `permission` → included, but the user lacks the access-smart-assist slug
  const params = new URLSearchParams(window.location.search)
  if (params.get('auth_error') === 'entitlement') {
    const reason = params.get('reason')
    if (reason === 'permission') {
      loginError.value =
        'While included in your subscription, your user account does not have access to this service. Please contact your FORESIGHT representative to request access.'
    } else if (reason === 'service') {
      loginError.value =
        'Your subscription does not include this service. Please contact your FORESIGHT representative to request access.'
    } else {
      loginError.value =
        'Your account is not enabled for this service. Please contact your FORESIGHT representative to request access.'
    }
    params.delete('auth_error')
    params.delete('reason')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }
}

export function useAuth() {
  const isAuthenticated = computed(() => !!subjectId.value)

  async function checkAuth() {
    consumeLoginErrorParam()
    try {
      const res = await apiFetch('/auth/session/me')
      if (res.ok) {
        const data: SessionIdentity = await res.json()
        userName.value = data.name || data.email || ''
        userEmail.value = data.email || ''
        subjectId.value = data.subject_id || ''
        clientId.value = data.client_id || ''
        showLoginPage.value = false
        resetAuthErrorState()
        startSessionWatch()
      } else {
        showLoginPage.value = true
      }
    } catch {
      showLoginPage.value = true
    } finally {
      authChecked.value = true
    }
  }

  function startLogin(email?: string) {
    loginError.value = ''
    const params = new URLSearchParams()
    const trimmed = (email || '').trim()
    if (trimmed) params.set('email', trimmed)
    const qs = params.toString()
    window.location.href = `/auth/session/login${qs ? '?' + qs : ''}`
  }

  function handleAuthFailure() {
    onSessionInvalid()
  }

  onAuthError(handleAuthFailure)

  async function logout() {
    stopSessionWatch()
    try {
      await apiFetch('/auth/session/logout', { method: 'POST' })
    } catch {
      // Best effort
    }
    clearIdentity()
    showLoginPage.value = true
  }

  return {
    userName,
    userEmail,
    subjectId,
    clientId,
    isAuthenticated,
    showLoginPage,
    authChecked,
    loginError,
    checkAuth,
    startLogin,
    logout,
    handleAuthFailure,
  }
}
